import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext.js';

interface RouteGuardProps {
  allowedRoles: string[];
  children: React.ReactNode;
  redirectTo?: string;
}

/**
 * RouteGuard component that protects routes based on user roles
 * @param allowedRoles - Array of roles that are allowed to access the route
 * @param children - React children to render if user has allowed role
 * @param redirectTo - Path to redirect to if user doesn't have permission (defaults to /unauthorized)
 */
export const RouteGuard: React.FC<RouteGuardProps> = ({ 
  allowedRoles, 
  children, 
  redirectTo = '/unauthorized' 
}) => {
  const location = useLocation();
  const { user } = useAuth();

  // Read roles from React in-memory state (AuthContext) — not localStorage.
  // localStorage is writable from DevTools/XSS and must not be trusted for authorization decisions.
  const userRoles: string[] = user?.roles ?? [];
  
  // If no roles specified, allow access to everyone
  if (!allowedRoles || allowedRoles.length === 0) {
    return <>{children}</>;
  }
  
  // Check if user has at least one of the allowed roles
  const hasAllowedRole = userRoles.some(role => allowedRoles.includes(role));
  
  // Special case: admin role can access everything
  const isAdmin = userRoles.includes('admin');
  
  if (hasAllowedRole || isAdmin) {
    return <>{children}</>;
  }
  
  // Redirect to unauthorized page or specified redirect path
  return <Navigate to={redirectTo} state={{ from: location }} replace />;
};

export default RouteGuard;
